/**
 * (owner, Sep 18: "when doing planning, the normal map should be replaced with a planning map that shows where zoning and
 * planning can be done; quick buttons / keyboard shortcuts for doing the planning, lit when you walk to a place that can
 * be zoned or where a road can be planned") The planner's geometry, pure and shared by the server (the shift's rules in
 * growth.ts), the client (the strip of quick buttons and the planning layer on the map, src/engine/hud.ts) and the tests:
 * which lattice piece can be staked from where, why not, the buttons' state at a spot, and the nearest piece that can be
 * staked. docs/DESIGN_GROWTH.md.
 *
 * A piece is a 16-tile stretch of the lattice (world/overlay.ts). It can be planned beyond the city's edge (`Fields.wild`
 * at its middle: the settlement's density under 2 % — further out than the last house), on no generated road, over no
 * water or railway, and it must touch a road at one end: a generated one — on the path world one within `ATTACH_REACH`
 * tiles along the piece's own line, the gap paved with the piece (MAPGEN_DECISION.md §31; the owner never found a spot
 * to plan on the live world because the piece end had to sit exactly on a bendy road) — or a planned or built piece.
 */
import type { ZoneName } from '../content/types';
import { Overlay, PIECE, type RoadPiece } from '../world/overlay';
import type { Axis, RoadHit } from '../world/roads';

export interface PlanNet {
  hitsAt(x: number, y: number, out?: RoadHit[]): RoadHit[];
  generatedAt(axis: Axis, t: number, u: number): boolean;
  /** the path world (path-adapter.ts): the gap from a piece end to the nearest kerb along the piece's own line, looking `only` one way */
  attachment?(axis: Axis, pos: number, e: number, only?: 1 | -1): { gap: number } | undefined;
}
export interface PlanWorld { gen: { net: PlanNet; fields: { wild(x: number, y: number): boolean; terrainAt(x: number, y: number): { buildable: boolean; walkable: boolean } } }; overlay: Overlay }

export const ZONE_CHOICES: ZoneName[] = ['suburb', 'downtown', 'industrial', 'park'];
export const ZONE_GLYPH: Record<string, string> = { suburb: '🏠', downtown: '🏢', industrial: '🏭', park: '🌳' };

/** The lattice piece nearest the player along each axis. */
export function piecesAround(x: number, y: number): { axis: Axis; pos: number; f: number }[] {
  return [
    { axis: 'v', pos: Math.round(x / PIECE) * PIECE, f: Math.floor(y / PIECE) * PIECE },
    { axis: 'h', pos: Math.round(y / PIECE) * PIECE, f: Math.floor(x / PIECE) * PIECE },
  ];
}
const tileOf = (axis: Axis, pos: number, u: number): [number, number] => (axis === 'v' ? [pos, u] : [u, pos]);
function roadAt(w: PlanWorld, axis: Axis, pos: number, u: number): boolean { const [x, y] = tileOf(axis, pos, u); return w.gen.net.hitsAt(x, y).some((h) => h.road.class !== 'rail'); }
/** Does the end of a piece (its first tile `f` looking back, or the tile past its last looking on: `side`) touch anything drivable — a generated road (or one within reach along the line, on the path world), a built or planned piece? */
export function endTouches(w: PlanWorld, axis: Axis, pos: number, f: number, side: -1 | 1): boolean {
  const ov = w.overlay, perp: Axis = axis === 'v' ? 'h' : 'v', e = side < 0 ? f : f + PIECE;
  if (roadAt(w, axis, pos, e) || (side < 0 && roadAt(w, axis, pos, e - 1))) return true;
  if (ov.piece(axis, pos, e) || ov.piece(axis, pos, e - PIECE)) return true;
  if (ov.piece(perp, e, pos) || ov.piece(perp, e, pos - PIECE)) return true;
  return !!w.gen.net.attachment?.(axis, pos, side < 0 ? e - 1 : e, side);
}
/** the tiles to pave past each end of a piece so it meets the kerb of the road it attaches to (0 where the end is on a road already, or on the lattice) */
export function pieceGaps(w: PlanWorld, axis: Axis, pos: number, f: number): [number, number] {
  const net = w.gen.net; if (!net.attachment) return [0, 0];
  const gap = (e: number, side: -1 | 1): number => (roadAt(w, axis, pos, e) ? 0 : net.attachment!(axis, pos, e, side)?.gap ?? 0);
  return [gap(f - 1, -1), gap(f + PIECE, 1)];
}
/** Why a piece cannot be planned here, or '' when it can. */
export function planCheck(w: PlanWorld, axis: Axis, pos: number, f: number): string {
  const net = w.gen.net, f2 = w.gen.fields, ov = w.overlay;
  if (ov.piece(axis, pos, f)) return 'already planned';
  const mid = f + PIECE / 2;
  const [mx, my] = tileOf(axis, pos, mid);
  if (!f2.wild(mx, my)) return 'inside the city — plan beyond its edge';
  if (net.generatedAt(axis, pos, mid)) return 'a road is already here';
  for (let u = f; u < f + PIECE; u++) { const [x, y] = tileOf(axis, pos, u); const t = f2.terrainAt(x, y); if (!t.buildable || !t.walkable) return 'water in the way'; if (net.hitsAt(x, y).some((h) => h.road.class === 'rail')) return 'a railway in the way'; }
  if (!endTouches(w, axis, pos, f, -1) && !endTouches(w, axis, pos, f, 1)) return 'must touch a road at one end';
  return '';
}
/** Why the block under (x, y) cannot be zoned, or '' */
export function zoneCheck(w: PlanWorld, x: number, y: number): string {
  const f2 = w.gen.fields;
  const [cx, cy] = Overlay.cellOf(x, y);
  const mx = cx * PIECE + PIECE / 2, my = cy * PIECE + PIECE / 2;
  if (!f2.wild(mx, my)) return 'inside the city';
  if (!f2.terrainAt(mx, my).buildable) return 'water';
  return '';
}

/** one quick button of the planner's strip: the work row it sends (`id`, 'none' when it cannot), its key on a keyboard, and why it is dark */
export interface PlanButton { id: string; key: string; glyph: string; label: string; on: boolean; why?: string; act: 'plan' | 'clear' | 'none' }
/** The strip's seven buttons where the player stands: the street north–south (1) and east–west (2), the four zones (3–6), clear the zoning (0). The ids are the J list's own rows (growth.ts `workRows`), so the server needs nothing new to act on them. */
export function planOptions(w: PlanWorld, x: number, y: number, opts: { inside?: boolean; zoneName?: (z: ZoneName) => string } = {}): PlanButton[] {
  const out: PlanButton[] = [], ov = w.overlay;
  piecesAround(x, y).forEach((c, i) => {
    const dir = c.axis === 'v' ? 'N–S' : 'E–W', glyph = c.axis === 'v' ? '↕' : '↔', key = String(i + 1), label = `Street ${dir}`;
    const cur = ov.piece(c.axis, c.pos, c.f);
    if (opts.inside) out.push({ id: 'none', key, glyph, label, on: false, why: 'step outside', act: 'none' });
    else if (cur) out.push(cur.b > 0 ? { id: 'none', key, glyph, label, on: false, why: `${cur.b}/${PIECE} paved`, act: 'none' } : { id: `unplan:${c.axis}:${c.pos}:${c.f}`, key, glyph, label: `Clear ${dir}`, on: true, act: 'clear' });
    else { const why = planCheck(w, c.axis, c.pos, c.f); out.push({ id: why ? 'none' : `plan:${c.axis}:${c.pos}:${c.f}`, key, glyph, label, on: !why, ...(why ? { why } : {}), act: why ? 'none' : 'plan' }); }
  });
  const zwhy = opts.inside ? 'step outside' : zoneCheck(w, x, y), cur = ov.zoneAt(x, y);
  ZONE_CHOICES.forEach((z, i) => { const same = cur === z; out.push({ id: zwhy || same ? 'none' : `zone:${z}`, key: String(i + 3), glyph: ZONE_GLYPH[z], label: opts.zoneName?.(z) ?? z, on: !zwhy && !same, ...(zwhy ? { why: zwhy } : same ? { why: 'zoned so already' } : {}), act: same ? 'none' : 'plan' }); });
  out.push({ id: !zwhy && cur ? 'unzone' : 'none', key: '0', glyph: '✕', label: 'Clear zoning', on: !zwhy && !!cur, ...(zwhy ? { why: zwhy } : !cur ? { why: 'not zoned' } : {}), act: 'clear' });
  return out;
}

/** the pieces that can be staked within `cells` blocks of a spot (the map's planning layer, the objective's hint): the cheap test first, the full one only past it */
export function plannableNear(w: PlanWorld, x: number, y: number, cells: number): { axis: Axis; pos: number; f: number; x: number; y: number }[] {
  const out: { axis: Axis; pos: number; f: number; x: number; y: number }[] = [];
  const [cx, cy] = Overlay.cellOf(x, y), f2 = w.gen.fields;
  for (let j = cy - cells; j <= cy + cells; j++) for (let i = cx - cells; i <= cx + cells; i++) {
    for (const c of [{ axis: 'v' as Axis, pos: i * PIECE, f: j * PIECE }, { axis: 'h' as Axis, pos: j * PIECE, f: i * PIECE }]) {
      const [mx, my] = tileOf(c.axis, c.pos, c.f + PIECE / 2);
      if (!f2.wild(mx, my) || w.overlay.piece(c.axis, c.pos, c.f)) continue;
      if (planCheck(w, c.axis, c.pos, c.f)) continue;
      out.push({ ...c, x: mx, y: my });
    }
  }
  return out;
}
/** the nearest piece that can be staked, searched ring by ring up to `maxCells` blocks out */
export function nearestPlannable(w: PlanWorld, x: number, y: number, maxCells = 24): { axis: Axis; pos: number; f: number; x: number; y: number; d: number } | undefined {
  const [cx, cy] = Overlay.cellOf(x, y), f2 = w.gen.fields;
  let best: { axis: Axis; pos: number; f: number; x: number; y: number; d: number } | undefined;
  for (let r = 0; r <= maxCells; r++) {
    if (best && best.d < (r - 1) * PIECE) break; // (a ring further out cannot beat it)
    for (let j = cy - r; j <= cy + r; j++) for (let i = cx - r; i <= cx + r; i++) {
      if (Math.max(Math.abs(i - cx), Math.abs(j - cy)) !== r) continue;
      for (const c of [{ axis: 'v' as Axis, pos: i * PIECE, f: j * PIECE }, { axis: 'h' as Axis, pos: j * PIECE, f: i * PIECE }]) {
        const [mx, my] = tileOf(c.axis, c.pos, c.f + PIECE / 2);
        if (!f2.wild(mx, my) || w.overlay.piece(c.axis, c.pos, c.f)) continue;
        const d = Math.hypot(mx - x, my - y); if (best && d >= best.d) continue;
        if (planCheck(w, c.axis, c.pos, c.f)) continue;
        best = { ...c, x: mx, y: my, d };
      }
    }
  }
  return best;
}
export type { RoadPiece };
